import {
  Button,
  ButtonGroup,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, HStack, Show, Space, Text, VStack} from 'platform/foundation';

import {ChangeEvent, useEffect, useRef, useState} from 'react';

import {useUploadFileMutation} from '@dms/api/base';
import {
  ClaraClaimAttachment,
  useGetServiceCaseIntegrationsClaraClaimQuery,
  usePostServiceCaseIntegrationsClaraClaimMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {TestIdProps, suffixTestId} from 'shared/web';

import {ClaraSendFormSchema, ClaraSendFormType} from '../types/ClaraSendFormType';
import {ClaraClaimAttachmentItem} from './ClaraClaimAttachmentItem';
import {
  ClaraSelectFromServiceCaseModal,
  ClaraSelectedFile,
} from './ClaraSelectFromServiceCaseModal';

const MAX_FILE_SIZE = 6 * 1024 * 1024;
const ALLOWED_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'] as const;

const ACCEPTED_FILE_TYPES = ALLOWED_CONTENT_TYPES.join(',');

type AllowedContentType = (typeof ALLOWED_CONTENT_TYPES)[number];

function isAllowedContentType(value: string): value is AllowedContentType {
  return (ALLOWED_CONTENT_TYPES as readonly string[]).includes(value);
}

interface ClaraSendDialogProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  onClose: () => void;
}

export function ClaraSendDialog(props: ClaraSendDialogProps) {
  const {
    data: defaultValues,
    isLoading: isLoadingDefaultValues,
    isError: isDefaultValuesError,
  } = useGetServiceCaseIntegrationsClaraClaimQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {refetchOnMountOrArgChange: true}
  );

  const [postServiceCaseIntegrationsClaraClaim] =
    usePostServiceCaseIntegrationsClaraClaimMutation();

  const [attachments, setAttachments] = useState<ClaraClaimAttachment[]>([]);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, {isLoading: isUploading}] = useUploadFileMutation();

  useEffect(() => {
    if (defaultValues?.attachments?.length) {
      setAttachments(defaultValues.attachments);
    }
  }, [defaultValues]);

  const formDefaultValues = defaultValues
    ? {
        claimType: defaultValues.claimType,
        repairStartDate: defaultValues.repairStartDate,
        mileageStart: defaultValues.mileageStart ?? undefined,
        repairEndDate: defaultValues.repairEndDate,
        mileageEnd: defaultValues.mileageEnd ?? undefined,
        partPurchaseDate: defaultValues.partPurchaseDate,
        mileagePartPurchase: defaultValues.mileagePartPurchase,
        partNumber: defaultValues.partNumber,
      }
    : undefined;

  const handleFileInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        showNotification.error(i18n.t('entity.claimType.errors.fileTooLarge'));
        continue;
      }
      if (!isAllowedContentType(file.type)) {
        showNotification.error(i18n.t('entity.claimType.errors.fileTypeNotAllowed'));
        continue;
      }
      const contentType = file.type;

      await uploadFile({file})
        .unwrap()
        .then((result) => {
          setAttachments((prev) => [
            ...prev,
            {
              fileId: result.fileId,
              filename: file.name,
              contentType,
            },
          ]);
        })
        .catch(handleApiError);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFilesSelected = (files: ClaraSelectedFile[]) => {
    setAttachments((prev) => [
      ...prev,
      ...files.filter((f) => !prev.some((a) => a.fileId === f.fileId)),
    ]);
  };

  const handleSubmit: FormSubmitHandler<ClaraSendFormType> = async (data) => {
    await postServiceCaseIntegrationsClaraClaim({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      body: {
        ...data,
        partNumber: data.claimType === '1' ? data.partNumber : undefined,
        partPurchaseDate: data.claimType === '1' ? data.partPurchaseDate : undefined,
        mileagePartPurchase: data.claimType === '1' ? data.mileagePartPurchase : undefined,
        attachments,
      },
    })
      .unwrap()
      .then(props.onClose)
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isLoadingDefaultValues} isError={isDefaultValuesError}>
      <Form<ClaraSendFormType>
        onSubmit={handleSubmit}
        defaultValues={formDefaultValues}
        experimentalZodSchema={ClaraSendFormSchema}
      >
        {(control, formApi) => (
          <>
            <Grid spacing={4} columns={2}>
              <FormField
                control={control}
                name="claimType"
                type="choice"
                label={i18n.t('entity.claimType.labels.claimType')}
                options={[
                  {label: i18n.t('entity.vehicle.labels.vehicle'), value: '0'},
                  {label: i18n.t('entity.claimType.labels.part'), value: '1'},
                ]}
                isNotClearable
                isRequired
                data-testid={suffixTestId('claimType', props)}
              />
              <Show when={formApi.watch('claimType') === '0'}>
                <Space />
              </Show>
              <Show when={formApi.watch('claimType') === '1'}>
                <FormField
                  control={control}
                  name="partNumber"
                  type="text"
                  label={i18n.t('entity.claimType.labels.partNumber')}
                  isRequired
                />
                <FormField
                  control={control}
                  name="partPurchaseDate"
                  type="apiDate"
                  label={i18n.t('entity.claimType.labels.partPurchaseDate')}
                  isRequired
                />
                <FormField
                  control={control}
                  name="mileagePartPurchase"
                  type="number"
                  label={i18n.t('entity.claimType.labels.mileagePartPurchase')}
                  suffix={i18n.t('general.metric.km')}
                  isRequired
                />
              </Show>
              <FormField
                control={control}
                name="repairStartDate"
                type="apiDate"
                label={i18n.t('entity.claimType.labels.repairStartDate')}
                isRequired
              />
              <FormField
                control={control}
                name="mileageStart"
                type="number"
                label={i18n.t('entity.claimType.labels.mileageStart')}
                suffix={i18n.t('general.metric.km')}
                isRequired
              />
              <FormField
                control={control}
                name="repairEndDate"
                type="apiDate"
                label={i18n.t('entity.claimType.labels.repairEndDate')}
                isRequired
              />
              <FormField
                control={control}
                name="mileageEnd"
                type="number"
                label={i18n.t('entity.claimType.labels.mileageEnd')}
                suffix={i18n.t('general.metric.km')}
                isRequired
              />
            </Grid>
            <Separator />
            <VStack spacing={2}>
              <HStack spacing={4}>
                <Button
                  variant="link"
                  leftIcon="content/add_circle"
                  title={i18n.t('entity.claimType.actions.addFilesFromServiceCase')}
                  onClick={() => setIsSelectModalOpen(true)}
                  data-testid={suffixTestId('addFromServiceCase', props)}
                />
                <Button
                  variant="link"
                  leftIcon="action/backup"
                  title={i18n.t('entity.claimType.actions.uploadFilesFromDevice')}
                  onClick={() => fileInputRef.current?.click()}
                  isLoading={isUploading}
                  data-testid={suffixTestId('uploadFromDevice', props)}
                />
              </HStack>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.claimType.labels.attachmentHint')}
              </Text>
              {attachments.map((attachment) => (
                <ClaraClaimAttachmentItem
                  key={attachment.fileId}
                  attachment={attachment}
                  onRemove={() =>
                    setAttachments((prev) => prev.filter((a) => a.fileId !== attachment.fileId))
                  }
                  data-testid={suffixTestId('attachmentItem', props)}
                />
              ))}
            </VStack>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPTED_FILE_TYPES}
              style={{display: 'none'}}
              onChange={handleFileInputChange}
            />
            {isSelectModalOpen && (
              <ClaraSelectFromServiceCaseModal
                serviceCaseId={props.serviceCaseId}
                onClose={() => setIsSelectModalOpen(false)}
                onFilesSelected={handleFilesSelected}
                data-testid={suffixTestId('selectFromServiceCase', props)}
              />
            )}
            <DialogFooter>
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.discard')}
                  variant="secondary"
                  onClick={props.onClose}
                  data-testid={suffixTestId('discard', props)}
                />
                <FormButton
                  control={control}
                  title={i18n.t('entity.orderIntegration.actions.send')}
                  type="submit"
                  data-testid={suffixTestId('confirm', props)}
                />
              </ButtonGroup>
            </DialogFooter>
          </>
        )}
      </Form>
    </DataStatus>
  );
}
